import React from "react";
import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Input,
  Stack,
  Text,
} from "@chakra-ui/react";
import { MdClose, MdPersonAddAlt } from "react-icons/md";
import { emptyGuestRow, normalizeGuestList } from "./formsLogic";
import { ACCENT, DANGER, fieldProps, invalidFieldProps } from "./formsTheme";

const RowField = ({ label, error, ...inputProps }) => (
  <Box>
    <Text fontSize="10px" color="gray.400" fontWeight={600} mb={1}>
      {label}
    </Text>
    <Input
      h="42px"
      {...fieldProps}
      {...(error ? invalidFieldProps : {})}
      {...inputProps}
    />
    {error && (
      <Text fontSize="10px" color={DANGER} mt={1}>
        {error}
      </Text>
    )}
  </Box>
);

/**
 * Repeater for "everyone staying" questions. Always renders at least one
 * row so the guest has somewhere to type; rows left completely empty are
 * dropped at submit time by `compactGuestList`.
 */
export const GuestListInput = ({
  value,
  disabled,
  rowErrors = {},
  onChange,
}) => {
  const rows = normalizeGuestList(value);

  const updateRow = (index, field, fieldValue) => {
    const next = rows.map((row, i) =>
      i === index ? { ...row, [field]: fieldValue } : row
    );
    onChange(next);
  };

  const addRow = () => onChange([...rows, emptyGuestRow()]);

  const removeRow = (index) => {
    const next = rows.filter((_, i) => i !== index);
    onChange(next.length ? next : [emptyGuestRow()]);
  };

  return (
    <Stack spacing={3}>
      {rows.map((row, index) => {
        const errors = rowErrors[index] || {};
        return (
          <Box
            key={index}
            p={3}
            borderRadius="14px"
            bg="whiteAlpha.50"
            border="1px solid"
            borderColor={
              Object.keys(errors).length ? "rgba(248, 113, 113, 0.45)" : "whiteAlpha.100"
            }
          >
            <Flex align="center" justify="space-between" mb={2}>
              <Text
                fontSize="10px"
                fontWeight={700}
                textTransform="uppercase"
                letterSpacing="0.06em"
                color="gray.500"
              >
                {index === 0 ? "Primary guest" : `Guest ${index + 1}`}
              </Text>
              {rows.length > 1 && (
                <IconButton
                  aria-label={`Remove guest ${index + 1}`}
                  icon={<Icon as={MdClose} fontSize="14px" />}
                  size="xs"
                  variant="ghost"
                  color="whiteAlpha.600"
                  isDisabled={disabled}
                  _hover={{ bg: "whiteAlpha.100", color: "white" }}
                  onClick={() => removeRow(index)}
                />
              )}
            </Flex>
            <Stack spacing={2.5}>
              <RowField
                label="Name"
                placeholder="Full name"
                value={row.name}
                error={errors.name}
                isDisabled={disabled}
                onChange={(e) => updateRow(index, "name", e.target.value)}
              />
              <Flex gap={2.5} direction={{ base: "column", sm: "row" }}>
                <Box flex="1" minW={0}>
                  <RowField
                    label="Phone (optional)"
                    type="tel"
                    inputMode="tel"
                    placeholder="+1 555 123 4567"
                    value={row.phone}
                    error={errors.phone}
                    isDisabled={disabled}
                    onChange={(e) => updateRow(index, "phone", e.target.value)}
                  />
                </Box>
                <Box flex="1" minW={0}>
                  <RowField
                    label="Email (optional)"
                    type="email"
                    inputMode="email"
                    placeholder="name@email.com"
                    value={row.email}
                    error={errors.email}
                    isDisabled={disabled}
                    onChange={(e) => updateRow(index, "email", e.target.value)}
                  />
                </Box>
              </Flex>
            </Stack>
          </Box>
        );
      })}

      <Button
        onClick={addRow}
        isDisabled={disabled}
        variant="outline"
        h="42px"
        borderRadius="12px"
        fontSize="13px"
        fontWeight={700}
        color={ACCENT}
        borderColor="whiteAlpha.300"
        leftIcon={<Icon as={MdPersonAddAlt} fontSize="16px" />}
        _hover={{ bg: "whiteAlpha.100", borderColor: ACCENT }}
        _active={{ bg: "whiteAlpha.200" }}
      >
        Add another guest
      </Button>
    </Stack>
  );
};

export default GuestListInput;
